import React, { useEffect, useState } from 'react'
import TableContainer from '@mui/material/TableContainer'
import Table from '@mui/material/Table'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import TableBody from '@mui/material/TableBody'
import TablePagination from '@mui/material/TablePagination'
import Paper from '@mui/material/Paper'
import { useTranslation } from 'react-i18next'
import {
  calendar,
  copy,
  deleteIcon,
  editIcon,
  emptyImage,
  eye,
  goToIcon,
  points,
  scan,
  tag,
} from '../../images'
import { useNavigate } from 'react-router-dom'
import { TableHead } from '@mui/material'
import { toast } from 'react-toastify'
import { formatDate } from '../../helpers/utils'

interface utmValues {
  origin: string
  media: string
  campaign: string
  term: string
  content: string
}

interface LinkValues {
  _id: string
  original_url: string
  short_url: string
  title: string
  domain: string
  custom_code: string
  tags: string[]
  has_qr: boolean
  utm: utmValues
  status: number
  createdAt: string
  tracking_count: number
}
type LinkListProps = {
  dataBody: LinkValues[]
  totalRecords: number
  totalPages: number
  elevation?: number
  dataSelected: (linkCode: LinkValues) => void
  paginator?: boolean
  setOpenEdit: (value: boolean) => void
  setOpenDelete: (value: boolean) => void
  arrayIds: Array<string>
  getSelectedIds: (newArrayIds: Array<string>) => void
  setArrayIds: React.Dispatch<React.SetStateAction<Array<string>>>
  onCounterChange: (value: number) => void
  onPageChange: (value: number) => void
}

const LinkList = ({
  dataBody,
  totalRecords,
  totalPages,
  elevation = 1,
  dataSelected,
  setOpenEdit,
  setOpenDelete,
  paginator = true,
  arrayIds,
  setArrayIds,
  getSelectedIds,
  onCounterChange,
  onPageChange,
}: LinkListProps) => {
  const { t } = useTranslation()
  const [showMenuExtra, setShowMenuExtra] = useState(false)
  const [cardIndex, setCardIndex] = useState(-1)
  let navigate = useNavigate()
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(50)
  const [selectAll, setSelectAll] = useState(false)
  const url = '/links/'

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage)
    onCounterChange(newPage + 1)
    onPageChange(newPage)
  }

  const handleCheckedId = (
    e: React.ChangeEvent<HTMLInputElement>,
    itemId: string
  ) => {
    const item = itemId

    if (e.target.checked) {
      if (!arrayIds.includes(item)) {
        const newArrayIds = [...arrayIds, item]
        setArrayIds(newArrayIds)
        getSelectedIds(newArrayIds)
      }
    } else {
      const newArrayIds = arrayIds.filter((id: string) => id !== item)
      setArrayIds(newArrayIds)
      getSelectedIds(newArrayIds)
    }
  }

  const handleSelectAllChange = () => {
    setSelectAll(!selectAll)
    if (!selectAll) {
      const allIds = dataBody?.map((item) => item._id).filter(Boolean)
      if (allIds) {
        setArrayIds(allIds)
        getSelectedIds(allIds)
      }
    } else {
      setArrayIds([])
      getSelectedIds([])
    }
  }

  const handleMenuExtra = (index: number) => {
    setShowMenuExtra(!showMenuExtra)
    setCardIndex(index)
  }

  const handleEdit = (linkData: LinkValues) => {
    dataSelected(linkData)
    setOpenEdit(true)
    setShowMenuExtra(false)
  }

  const handleDelete = (linkData: LinkValues) => {
    dataSelected(linkData)
    setOpenDelete(true)
    setShowMenuExtra(false)
  }

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(+event.target.value)
    setPage(0)
  }

  const copyLink = (url: string) => {
    navigator.clipboard.writeText(url)
    toast(`${t('LINK_COPIED')}`)
  }

  const goTo = (url: string, blank: boolean) => {
    if (blank) window.open(url)
    else window.location.href = url
  }
  const MenuExtra = (linkData: LinkValues) => {
    return (
      <div className='container-option-extra'>
        <button
          className='button-option-extra'
          onClick={() => goTo(linkData.short_url, true)}
        >
          <img
            src={goToIcon}
            alt={`${t('GO_TO_LINK')}`}
            className='icon-option-extra'
          />
          <span className='option-extra'>{`${t('GO_TO_LINK')}`}</span>
        </button>
        <hr></hr>
        <button
          className='button-option-extra'
          onClick={() => handleEdit(linkData)}
        >
          <img
            src={editIcon}
            alt={`${t('EDIT')}`}
            className='icon-option-extra'
          />
          <span className='option-extra'>{`${t('EDIT')}`}</span>
        </button>
        <hr></hr>
        <button
          className='button-option-extra'
          onClick={() => {
            copyLink(linkData.short_url)
          }}
        >
          <img src={copy} alt={`${t('COPY')}`} className='icon-option-extra' />
          <span className='option-extra'>{`${t('COPY')}`}</span>
        </button>
        <hr></hr>
        <button
          className='button-option-extra'
          onClick={() => navigate(`/links/${linkData._id}`)}
        >
          <img
            src={eye}
            alt={`${t('VIEW_DETAIL')}`}
            className='icon-option-extra'
          />
          <span className='option-extra'>{`${t('VIEW_DETAIL')}`}</span>
        </button>
        <hr></hr>
        <button
          className='button-option-extra'
          onClick={() => handleDelete(linkData)}
        >
          <img
            src={deleteIcon}
            alt={`${t('DELETE')}`}
            className='icon-option-extra'
          />
          <span className='option-extra'>{`${t('DELETE')}`}</span>
        </button>
      </div>
    )
  }

  return (
    <>
      <Paper elevation={elevation} className='table m-0 p-0'>
        <TableContainer className='table-container'>
          <TableHead>
            <input
              id='checkbox-select'
              type='checkbox'
              className='checkbox-round-filters'
              onChange={handleSelectAllChange}
              checked={selectAll}
            />
            <label htmlFor='checkbox-select'>{`${t('SELECT_ALL')}`}</label>
          </TableHead>
          <Table stickyHeader aria-label='sticky table' id='table-to-xls'>
            <TableBody>
              {dataBody && dataBody.length > 0 ? (
                dataBody.map((value, index) => (
                  <TableRow key={value._id}>
                    <TableCell>
                      <input
                        id={value._id}
                        type='checkbox'
                        className='checkbox-round'
                        onChange={(e) => handleCheckedId(e, value._id)}
                        checked={arrayIds.includes(value._id)}
                      />
                    </TableCell>
                    <TableCell>
                      <div className='link-card-icon-container'>
                        <img
                          alt={value.title}
                          src={`${value.original_url}/favicon.ico`}
                          className='img-card-link'
                          onError={(e) => {
                            const imgElement = e.target as HTMLImageElement
                            imgElement.src = emptyImage
                          }}
                        />
                      </div>
                    </TableCell>
                    <TableCell
                      className='pointer table-cell-custom'
                      onClick={() => navigate(`${url}${value._id}`)}
                    >
                      {value.title}
                    </TableCell>
                    <TableCell
                      className='pointer table-cell-custom'
                      onClick={() => navigate(`${url}${value._id}`)}
                    >
                      {value.short_url}
                    </TableCell>
                    <TableCell className='text-nowrap'>
                      <img
                        className='button-card-icon'
                        src={scan}
                        alt={`${t('SCAN')}`}
                      />
                      {value.tracking_count}
                    </TableCell>
                    <TableCell className='text-nowrap'>
                      <img
                        className='button-card-icon'
                        src={tag}
                        alt={`${t('TAG')}`}
                      />
                      {value.tags.length > 0 ? (
                        value.tags.map((elem: string, index: number) => (
                          <span key={elem}>
                            {index > 0 && ', '}
                            {t(elem)}
                          </span>
                        ))
                      ) : (
                        <span>{`${t('NO_TAGS')}`}</span>
                      )}
                    </TableCell>
                    <TableCell className='text-nowrap'>
                      <img
                        className='button-card-icon'
                        src={calendar}
                        alt={`${t('DATE')}`}
                      />
                      {formatDate(value.createdAt)}
                    </TableCell>
                    <TableCell>
                      <button
                        className='button-general'
                        type='button'
                        onClick={() => handleMenuExtra(index)}
                      >
                        <img
                          className='button-card-icon'
                          src={points}
                          alt={`${t('SHOW_MENU')}`}
                        />
                      </button>
                      {showMenuExtra && cardIndex === index && (
                        <MenuExtra {...value} />
                      )}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} align='center'>
                    {t('NO_DATA_AVAILABLE')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {!!dataBody?.length && paginator && (
          <div className='paginator'>
            <TablePagination
              rowsPerPageOptions={[]}
              component='div'
              count={totalRecords}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </div>
        )}
      </Paper>
    </>
  )
}

export default LinkList
